import { useEffect, useRef, useState } from 'react';

import { useApi } from '@backstage/core-plugin-api';

import { BitbucketProjectOption, bitbucketProjectsApiRef } from './BitbucketProjectsApi';
/**
 * Hook to fetch Bitbucket projects with loading and error states
 *
 * @param workspace - Bitbucket workspace slug (e.g., 'carrier-digital')
 * @param refreshTrigger - Optional trigger to force refetch (increment to refresh)
 * @returns Object containing projects array, loading state, and error
 */
export function useBitbucketProjects(workspace?: string, refreshTrigger?: number) {
  const bitbucketProjectsApi = useApi(bitbucketProjectsApiRef);
  const [projects, setProjects] = useState<BitbucketProjectOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  // Track request ID to prevent duplicate calls when refreshTrigger changes rapidly
  const requestIdRef = useRef<number>(0);
  const isRequestInFlightRef = useRef<boolean>(false);

  useEffect(() => {
    // Workspace is required for new API
    if (!workspace) {
      setError(
        new Error(
          'Bitbucket workspace not configured. Please provide workspace in ui:options',
        ),
      );
      return (): void => {
        // No cleanup needed
      };
    }

    // Skip if request already in flight (prevents duplicate API calls)
    if (isRequestInFlightRef.current) {
      // eslint-disable-next-line no-console
      console.log('Skipping API call - request already in flight', {
        workspace,
        refreshTrigger,
      });
      return (): void => {
        // No cleanup needed
      };
    }

    let isMounted = true;
    const currentRequestId = ++requestIdRef.current;
    isRequestInFlightRef.current = true;
    // Copy ref value at effect start to avoid stale closure in cleanup
    const requestIdAtStart = requestIdRef.current;

    const run = async () => {
      setLoading(true);
      setError(undefined);
      try {
        // Pass refresh=true when refreshTrigger > 0 to bypass cache
        const refresh = refreshTrigger !== undefined && refreshTrigger > 0;
        // Use the API client which handles discoveryApi and authentication
        const data = await bitbucketProjectsApi.getProjects(workspace, refresh);

        // Only update state if this is still the latest request (prevents race conditions)
        if (isMounted && currentRequestId === requestIdRef.current) {
          setProjects(data.projects);
        }
      } catch (e: any) {
        // Only update error if this is still the latest request
        if (isMounted && currentRequestId === requestIdRef.current) {
          setError(e);
          // DO-4126: Clear projects when error occurs (especially for 404/notFound errors)
          // This prevents showing stale data from previous workspace
          setProjects([]);
        }
      } finally {
        // Only update loading if this is still the latest request
        if (isMounted && currentRequestId === requestIdRef.current) {
          setLoading(false);
          isRequestInFlightRef.current = false;
        }
      }
    };

    run();

    return (): void => {
      isMounted = false;
      // Reset flag if this is still the current request (component unmounted)
      if (currentRequestId === requestIdAtStart) {
        isRequestInFlightRef.current = false;
      }
    };
  }, [workspace, bitbucketProjectsApi, refreshTrigger]);

  return { projects, loading, error };
}

/**
 * Validate that a project value exists in the list of projects
 */
export function validateBitbucketProject(
  value: string,
  projects: BitbucketProjectOption[],
): boolean {
  if (!value) return false;
  return projects.some(project => project.value === value);
}

// Re-export API types for convenience
export type { BitbucketProjectOption, BitbucketProjectsResponse } from './BitbucketProjectsApi';
